import { monkeyPatch } from "./monkeyPatch";

monkeyPatch(Map.prototype, {
  /**
   * ein effizientes .map(mapper).find(x=>x)
   * @param mapper
   * @returns
   */
  findMap<K, V, U>(
    this: Map<K, V>,
    mapper: (value: V, key: K, map: Map<K, V>) => U | null | undefined,
  ): U | undefined {
    for (const [key, value] of this as Map<K, V>) {
      const result = mapper(value, key, this as Map<K, V>);
      if (result != null) {
        return result;
      }
    }
    return undefined;
  },

  /**
   * sortiert die Keys der Map
   * @param sortFunction
   */
  sortKeys<K, V>(this: Map<K, V>, sortFunction?: (a: K, b: K) => number): Map<K, V> {
    const sorted = new Map<K, V>();
    const array = Array.from(this.keys());
    array.sort(sortFunction).forEach((key) => {
      const value = this.get(key);
      if (value !== undefined) {
        sorted.set(key, value);
      }
    });
    return sorted;
  },

  /**
   * ruft mapper(value,key) für jeden Eintrag der Map auf und baut ein Array mit den
   * Rückgabewerten auf
   * @param mapper
   * @returns
   */
  map<K, V, R>(this: Map<K, V>, mapper: (value: V, key: K, map: Map<K, V>) => R): R[] {
    const list: R[] = [];
    this.forEach((value, key) => {
      list.push(mapper(value, key, this as Map<K, V>));
    });
    return list;
  },

  /**
   * Reduce auf einer Map
   * @param reducer
   * @param startValue
   * @returns
   */
  reduce<K, V, R>(
    this: Map<K, V>,
    reducer: (previousValue: R, current: [K, V], index: number, entries: [K, V][]) => R,
    startValue: R,
  ): R {
    const entries = Array.from(this.entries());
    return entries.reduce(reducer, startValue);
  },

  /**
   * sucht nach erstem Element für das predicate true ergibt und gibt ein {key,value} zurück
   * @param predicate
   * @returns Object with key and value
   */
  find<K, V>(
    this: Map<K, V>,
    predicate: (value: V, key: K, map: Map<K, V>) => boolean,
  ): { key: K; value: V } | undefined {
    for (const [key, value] of this as Map<K, V>) {
      if (predicate(value, key, this as Map<K, V>)) {
        return { key, value };
      }
    }
    return undefined;
  },

  /**
   * Gibt eine Liste von {key,value} - entries zurück, auf welche Predicate passt
   * @param predicate
   * @returns Array mit Objekten {key,value}
   */
  filter<K, V>(
    this: Map<K, V>,
    predicate: (value: V, key: K, map: Map<K, V>) => boolean,
  ): Array<{ key: K; value: V }> {
    const entries: Array<{ key: K; value: V }> = [];
    for (const [key, value] of this as Map<K, V>) {
      if (predicate(value, key, this as Map<K, V>)) {
        entries.push({ key, value });
      }
    }
    return entries;
  },

  /**
   * reverse Lookup to find a key, to a given value
   * @param value
   * @returns key oder undefined
   */
  getKey<K, V>(this: Map<K, V>, value: V): K | undefined {
    const self = this as Map<K, V> & {
      find(
        predicate: (value: V, key: K, map: Map<K, V>) => boolean,
      ): { key: K; value: V } | undefined;
    };
    const r = self.find((v) => v === value);
    return r === undefined ? undefined : r.key;
  },

  /**
   * true wenn predicate für ein (value,key) - Paar true ist
   * @param predicate
   * @returns boolean
   */
  some<K, V>(this: Map<K, V>, predicate: (value: V, key: K, map: Map<K, V>) => boolean): boolean {
    for (const [key, value] of this as Map<K, V>) {
      if (predicate(value, key, this as Map<K, V>)) {
        return true;
      }
    }
    return false;
  },

  /**
   * true wenn predicate für alle (value,key) - Paare true ist
   * @param predicate
   * @returns boolean
   */
  every<K, V>(this: Map<K, V>, predicate: (value: V, key: K, map: Map<K, V>) => boolean): boolean {
    for (const [key, value] of this as Map<K, V>) {
      if (!predicate(value, key, this as Map<K, V>)) {
        return false;
      }
    }
    return true;
  },
});

/**
 * Welcher Vollpfosten hatte diese glorreiche Idee: array.length aber map.size???????
 * Sowas ist einfach nur kognitiver Müll!
 * Das hier ist zwar nicht so performant, aber solche Fehler sind teilweise schwer zu finden,
 * daher lieber Bugfreiheit.
 * for the record:
 * https://stackoverflow.com/questions/35038750/why-do-javascript-sets-maps-use-size-instead-of-length
 * Idioten!
 */
if (!Object.getOwnPropertyDescriptor(Map.prototype, "length")) {
  Object.defineProperty(Map.prototype, "length", {
    get(this: Map<unknown, unknown>): number {
      return this.size;
    },
  });
}
